import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { createContext, useCallback, useMemo, useState } from 'react';
import { templateApi } from 'src/api/template';
import {
  CreateTemplateDtoTemplateTypeEnum,
  GetTemplateDto,
  TemplatesApiTemplateControllerCreateTemplateRequest,
  TemplatesApiTemplateControllerDeleteTemplateRequest,
  TemplatesApiTemplateControllerUpdateTemplateRequest,
} from 'woofwoof-api';
import { TemplatesFiltersFormValuesProps } from '../components/TemplatesFiltersForm';
import { TemplatesFormModalValuesProps } from '../components/TemplatesFormModal';
import { translateKey } from 'src/locales/translate';

// ----------------------------------------------------------------------

// Default categories that should always be available
const defaultCategories = [
  'veterinarian',
  'dietitian',
  'behaviorist',
  'physiotherapist',
  'general',
];

// Polish translations for predefined categories
export const categoryTranslations: Record<string, string> = {
  veterinarian: translateKey("search.category.veterinarian"),
  dietitian: translateKey("search.category.dietitian"),
  behaviorist: translateKey("search.category.behaviorist"),
  physiotherapist: translateKey("search.category.physiotherapist"),
  general: translateKey("hardcoded.ogolny"),
};

export const templateTypeTranslations: Record<
  CreateTemplateDtoTemplateTypeEnum,
  string
> = {
  [CreateTemplateDtoTemplateTypeEnum.InitialAssessment]: translateKey("hardcoded.ocena_wstepna"),
  [CreateTemplateDtoTemplateTypeEnum.FollowUp]: 'Wizyta kontrolna',
  [CreateTemplateDtoTemplateTypeEnum.General]: translateKey("hardcoded.ogolny"),
  [CreateTemplateDtoTemplateTypeEnum.Surgery]: 'Operacja',
  [CreateTemplateDtoTemplateTypeEnum.Vaccination]: 'Szczepienie',
  [CreateTemplateDtoTemplateTypeEnum.Emergency]: translateKey("hardcoded.nagly_przypadek"),
  [CreateTemplateDtoTemplateTypeEnum.Diagnostic]: 'Diagnostyka',
  [CreateTemplateDtoTemplateTypeEnum.Prescription]: 'Recepta',
  [CreateTemplateDtoTemplateTypeEnum.Discharge]: 'Wypis',
  [CreateTemplateDtoTemplateTypeEnum.Checkup]: 'Kontrola',
};

export type TemplatesContextType = {
  handleCreateTemplate: (formValues: TemplatesFormModalValuesProps) => void;
  isCreateTemplateLoading: boolean;
  isModalOpen: boolean;
  handleOpenModal: (templateId?: string) => void;
  handleCloseModal: () => void;
  templatesData: GetTemplateDto[] | undefined;
  isTemplatesDataLoading: boolean;
  templateCategories: string[];
  isTemplateCategoriesLoading: boolean;
  selectedTemplateId: string;
  setSelectedTemplateId: (templateId: string) => void;
  filters: TemplatesFiltersFormValuesProps;
  setFilters: (filters: TemplatesFiltersFormValuesProps) => void;
  handleUpdateTemplate: (formValues: TemplatesFormModalValuesProps) => void;
  isUpdateTemplateLoading: boolean;
  handleDeleteTemplate: () => void;
  isDeleteTemplateLoading: boolean;
  isDeleteModalOpen: boolean;
  handleOpenDeleteModal: () => void;
  handleCloseDeleteModal: () => void;
};

export const TemplatesContext = createContext<TemplatesContextType | null>(
  null,
);

type TemplatesProviderProps = {
  children: React.ReactNode;
};

export function TemplatesProvider({ children }: TemplatesProviderProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [filters, setFilters] = useState<TemplatesFiltersFormValuesProps>({
    name: '',
    category: '',
    templateType: undefined,
  });
  const { enqueueSnackbar } = useSnackbar();

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setTimeout(() => {
      setSelectedTemplateId('');
    }, 500);
  }, []);

  const handleOpenModal = useCallback((templateId?: string) => {
    if (templateId) {
      setSelectedTemplateId(templateId);
    }
    setIsModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  const handleOpenDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  // Queries
  const {
    data: templatesData,
    isLoading: isTemplatesDataLoading,
    refetch: refetchTemplates,
  } = useQuery(
    ['templates', filters.name, filters.category, filters.templateType],
    () =>
      templateApi.templateControllerGetTemplates({
        category: filters.category || undefined,
        templateType: filters.templateType || undefined,
        name: filters.name || undefined,
      }),
  );

  const {
    data: templateCategories,
    isLoading: isTemplateCategoriesLoading,
    refetch: refetchTemplateCategories,
  } = useQuery(['template-categories'], () =>
    templateApi.templateControllerGetTemplateCategories(),
  );

  const { mutate: createTemplate, isLoading: isCreateTemplateLoading } =
    useMutation({
      mutationFn: (
        request: TemplatesApiTemplateControllerCreateTemplateRequest,
      ) => templateApi.templateControllerCreateTemplate(request),
      onSuccess: async () => {
        handleCloseModal();
        await refetchTemplates();
        await refetchTemplateCategories();
        enqueueSnackbar(translateKey("hardcoded.szablon_zostal_utworzony"), { variant: 'success' });
      },
      onError: (error) => {
        console.error('Error creating template:', error);
        enqueueSnackbar(translateKey("hardcoded.wystapil_blad_podczas_tworzenia_szablonu"), {
          variant: 'error',
        });
      },
    });

  const { mutate: updateTemplate, isLoading: isUpdateTemplateLoading } =
    useMutation({
      mutationFn: (
        request: TemplatesApiTemplateControllerUpdateTemplateRequest,
      ) => templateApi.templateControllerUpdateTemplate(request),
      onSuccess: async () => {
        handleCloseModal();
        await refetchTemplates();
        await refetchTemplateCategories();
        enqueueSnackbar(translateKey("hardcoded.szablon_zostal_zaktualizowany"), {
          variant: 'success',
        });
      },
      onError: (error) => {
        console.error('Error updating template:', error);
        enqueueSnackbar(translateKey("hardcoded.wystapil_blad_podczas_aktualizacji_szablonu"), {
          variant: 'error',
        });
      },
    });

  const { mutate: deleteTemplate, isLoading: isDeleteTemplateLoading } =
    useMutation({
      mutationFn: (
        request: TemplatesApiTemplateControllerDeleteTemplateRequest,
      ) => templateApi.templateControllerDeleteTemplate(request),
      onSuccess: async () => {
        handleCloseDeleteModal();
        handleCloseModal();
        await refetchTemplates();
        await refetchTemplateCategories();
        enqueueSnackbar(translateKey("hardcoded.szablon_zostal_usuniety"), { variant: 'success' });
      },
      onError: (error) => {
        console.error('Error deleting template:', error);
        enqueueSnackbar(translateKey("hardcoded.wystapil_blad_podczas_usuwania_szablonu"), {
          variant: 'error',
        });
      },
    });

  const handleCreateTemplate = useCallback(
    (formValues: TemplatesFormModalValuesProps) => {
      createTemplate({
        createTemplateDto: {
          name: formValues.name,
          description: formValues.description,
          category: formValues.category
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '_'),
          templateType: formValues.templateType,
          content: formValues.content,
          isShared: formValues.isShared,
        },
      });
    },
    [createTemplate],
  );

  const handleDeleteTemplate = useCallback(() => {
    deleteTemplate({ id: selectedTemplateId });
  }, [deleteTemplate, selectedTemplateId]);

  const handleUpdateTemplate = useCallback(
    (formValues: TemplatesFormModalValuesProps) => {
      updateTemplate({
        id: selectedTemplateId,
        updateTemplateDto: {
          name: formValues.name,
          description: formValues.description,
          category: formValues.category
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '_'),
          templateType: formValues.templateType,
          content: formValues.content,
          isShared: formValues.isShared,
        },
      });
    },
    [updateTemplate, selectedTemplateId],
  );

  const memoizedValue = useMemo(
    () => ({
      isModalOpen,
      handleOpenModal,
      handleCloseModal,
      handleCreateTemplate,
      handleUpdateTemplate,
      isCreateTemplateLoading,
      isUpdateTemplateLoading,
      templatesData: templatesData?.data,
      isTemplatesDataLoading,
      templateCategories: Array.from(
        new Set([...defaultCategories, ...(templateCategories?.data || [])]),
      ),
      isTemplateCategoriesLoading,
      selectedTemplateId,
      setSelectedTemplateId,
      filters,
      setFilters,
      handleDeleteTemplate,
      isDeleteTemplateLoading,
      isDeleteModalOpen,
      handleOpenDeleteModal,
      handleCloseDeleteModal,
    }),
    [
      handleCreateTemplate,
      isCreateTemplateLoading,
      isModalOpen,
      handleOpenModal,
      handleCloseModal,
      templatesData?.data,
      isTemplatesDataLoading,
      templateCategories?.data,
      isTemplateCategoriesLoading,
      filters,
      setFilters,
      selectedTemplateId,
      setSelectedTemplateId,
      isUpdateTemplateLoading,
      handleUpdateTemplate,
      handleDeleteTemplate,
      isDeleteTemplateLoading,
      isDeleteModalOpen,
      handleOpenDeleteModal,
      handleCloseDeleteModal,
    ],
  );

  return (
    <TemplatesContext.Provider value={memoizedValue}>
      {children}
    </TemplatesContext.Provider>
  );
}
